import React, { useState, useEffect, useCallback } from 'react';
import { Invoice } from '../types';
import { apiService } from '../services/api';

interface EmailPreviewProps {
  invoice: Invoice;
  language?: 'en' | 'de' | 'fr' | 'es' | 'es-ch' | 'pt' | 'pt-br';
  customMessage?: string;
  onClose: () => void;
}

interface EmailPreviewData {
  subject: string;
  html_body: string;
  recipient_email: string;
  language: string;
  to_recipients?: string[];
  cc_recipients?: string[];
  bcc_recipients?: string[];
}

const EmailPreview: React.FC<EmailPreviewProps> = ({
  invoice,
  language = 'en',
  customMessage,
  onClose
}) => {
  const [previewData, setPreviewData] = useState<EmailPreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEmailPreview = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.axiosInstance.post(
        `/api/v1/invoices/${invoice.invoice_id}/emails/preview`,
        {
          language: language,
          custom_message: customMessage || undefined,
          recipient_email: invoice.customer_email_address || 'preview@example.com'
        }
      );

      setPreviewData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load email preview');
    } finally {
      setLoading(false);
    }
  }, [invoice.invoice_id, invoice.customer_email_address, language, customMessage]);

  useEffect(() => {
    fetchEmailPreview();
  }, [fetchEmailPreview]);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow && previewData) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Email Preview - ${previewData.subject}</title>
          <style>
            body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
            .print-header { 
              border-bottom: 2px solid #ccc; 
              padding-bottom: 10px; 
              margin-bottom: 20px; 
            }
            .print-header h1 { margin: 0; color: #333; }
            .print-header p { margin: 5px 0; color: #666; }
            @media print {
              .print-header { border-bottom: 1px solid #000; }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h1>Email Preview</h1>
            <p><strong>Subject:</strong> ${previewData.subject}</p>
            <p><strong>To:</strong> ${(previewData.to_recipients && previewData.to_recipients.length > 0) ? previewData.to_recipients.join(', ') : previewData.recipient_email}</p>
            ${previewData.cc_recipients && previewData.cc_recipients.length ? `<p><strong>CC:</strong> ${previewData.cc_recipients.join(', ')}</p>` : ''}
            ${previewData.bcc_recipients && previewData.bcc_recipients.length ? `<p><strong>BCC:</strong> ${previewData.bcc_recipients.join(', ')}</p>` : ''}
            <p><strong>Language:</strong> ${previewData.language.toUpperCase()}</p>
          </div>
          ${previewData.html_body}
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  if (loading) {
    return (
      <div className="email-preview-modal">
        <div className="email-preview-content">
          <div className="email-preview-header">
            <h2>Email Preview</h2>
            <button onClick={onClose} className="close-button">×</button>
          </div>
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading email preview...</p>
          </div>
        </div>
        <style>{emailPreviewStyles}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="email-preview-modal">
        <div className="email-preview-content">
          <div className="email-preview-header">
            <h2>Email Preview</h2>
            <button onClick={onClose} className="close-button">×</button>
          </div>
          <div className="error-container">
            <div className="error-message">
              <h3>⚠️ Preview Error</h3>
              <p>{error}</p>
              <button onClick={fetchEmailPreview} className="retry-button">
                Try Again
              </button>
            </div>
          </div>
        </div>
        <style>{emailPreviewStyles}</style>
      </div>
    );
  }

  return (
    <div className="email-preview-modal">
      <div className="email-preview-content">
        <div className="email-preview-header">
          <h2>Email Preview</h2>
          <div className="header-actions">
            <button onClick={handlePrint} className="print-button">
              🖨️ Print
            </button>
            <button onClick={onClose} className="close-button">×</button>
          </div>
        </div>
        
        {previewData && (
          <>
            <div className="email-meta">
              <div className="meta-row">
                <span className="meta-label">Subject:</span>
                <span className="meta-value">{previewData.subject}</span>
              </div>
              <div className="meta-row">
                <span className="meta-label">To:</span>
                <span className="meta-value">{(previewData.to_recipients && previewData.to_recipients.length > 0) ? previewData.to_recipients.join(', ') : previewData.recipient_email}</span>
              </div>
              {previewData.cc_recipients && previewData.cc_recipients.length > 0 && (
                <div className="meta-row">
                  <span className="meta-label">CC:</span>
                  <span className="meta-value">{previewData.cc_recipients.join(', ')}</span>
                </div>
              )}
              {previewData.bcc_recipients && previewData.bcc_recipients.length > 0 && (
                <div className="meta-row">
                  <span className="meta-label">BCC:</span>
                  <span className="meta-value">{previewData.bcc_recipients.join(', ')}</span>
                </div>
              )}
              <div className="meta-row">
                <span className="meta-label">Language:</span>
                <span className="meta-value">{previewData.language.toUpperCase()}</span>
              </div>
            </div>
            
            <div className="email-preview-body">
              <iframe
                srcDoc={previewData.html_body}
                title="Email Preview"
                className="email-iframe"
                sandbox="allow-same-origin"
              />
            </div>
          </>
        )}
      </div>
      <style>{emailPreviewStyles}</style>
    </div>
  );
};

const emailPreviewStyles = `
  .email-preview-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
  }

  .email-preview-content {
    background: white;
    border-radius: 8px;
    width: 90%;
    max-width: 1000px;
    height: 90%;
    max-height: 800px;
    display: flex;
    flex-direction: column;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  }

  .email-preview-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #e0e0e0;
    background-color: #f8f9fa;
    border-radius: 8px 8px 0 0;
  }

  .email-preview-header h2 {
    margin: 0;
    color: #333;
    font-size: 24px;
  }

  .header-actions {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .print-button {
    background: #007bff;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .print-button:hover {
    background: #0056b3;
  }

  .close-button {
    background: #dc3545;
    color: white;
    border: none;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .close-button:hover {
    background: #c82333;
  }

  .email-meta {
    padding: 15px 20px;
    background-color: #f8f9fa;
    border-bottom: 1px solid #e0e0e0;
  }

  .meta-row {
    display: flex;
    margin-bottom: 8px;
  }

  .meta-row:last-child {
    margin-bottom: 0;
  }

  .meta-label {
    font-weight: 600;
    color: #495057;
    min-width: 80px;
    margin-right: 10px;
  }

  .meta-value {
    color: #212529;
    flex: 1;
  }

  .email-preview-body {
    flex: 1;
    padding: 0;
    overflow: hidden;
  }

  .email-iframe {
    width: 100%;
    height: 100%;
    border: none;
    background: white;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 300px;
    color: #666;
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #007bff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 20px;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 300px;
  }

  .error-message {
    text-align: center;
    color: #dc3545;
    max-width: 400px;
  }

  .error-message h3 {
    margin-bottom: 15px;
    font-size: 20px;
  }

  .error-message p {
    margin-bottom: 20px;
    line-height: 1.5;
  }

  .retry-button {
    background: #007bff;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
  }

  .retry-button:hover {
    background: #0056b3;
  }

  @media (max-width: 768px) {
    .email-preview-modal {
      padding: 10px;
    }

    .email-preview-content {
      width: 100%;
      height: 100%;
      max-width: none;
      max-height: none;
    }

    .email-preview-header {
      padding: 15px;
    }

    .email-preview-header h2 {
      font-size: 20px;
    }

    .meta-row {
      flex-direction: column;
      gap: 2px;
    }

    .meta-label {
      min-width: auto;
      margin-right: 0;
    }
  }
`;

export default EmailPreview;